import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, Switch, FormControlLabel, Chip, Button,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { usersAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../types';

interface UserFormData { username: string; email: string; fullName: string; isAdmin: boolean; isActive: boolean; }

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({ username: '', email: '', fullName: '', isAdmin: false, isActive: true });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try { const response = await usersAPI.getAll(); setUsers(response.data); setError(''); }
    catch (err) { setError('Failed to load users'); } finally { setLoading(false); }
  };

  const handleOpenDialog = (user: User | null = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({ username: user.Username, email: user.Email || '', fullName: user.FullName || '', isAdmin: !!user.IsAdmin, isActive: !!user.IsActive });
    } else {
      setEditingUser(null);
      setFormData({ username: '', email: '', fullName: '', isAdmin: true, isActive: true });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingUser(null); setFormData({ username: '', email: '', fullName: '', isAdmin: false, isActive: true }); };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        await usersAPI.update(editingUser.UserId, { email: formData.email, fullName: formData.fullName, isAdmin: formData.isAdmin, isActive: formData.isActive });
        setSuccess('User updated successfully');
      } else {
        await usersAPI.create({ username: formData.username, email: formData.email, fullName: formData.fullName, isAdmin: formData.isAdmin });
        setSuccess('User created successfully');
      }
      fetchUsers(); handleCloseDialog(); setError(''); setTimeout(() => setSuccess(''), 3000);
    } catch (err: unknown) { const axiosErr = err as { response?: { data?: { error?: string } } }; setError(axiosErr.response?.data?.error || 'Failed to save user'); }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await usersAPI.delete(id); fetchUsers(); setSuccess('User deactivated successfully'); setError(''); setTimeout(() => setSuccess(''), 3000); }
    catch (err: unknown) { const axiosErr = err as { response?: { data?: { error?: string } } }; setError(axiosErr.response?.data?.error || 'Failed to deactivate user'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4">User Management</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Manage admin privileges and user access</Typography>
        </Box>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()}>
          Add User
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Username</TableCell><TableCell>Full Name</TableCell><TableCell>Email</TableCell><TableCell>Role</TableCell><TableCell>Status</TableCell><TableCell>Last Login</TableCell><TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.UserId}>
                <TableCell>{user.Username}</TableCell><TableCell>{user.FullName || '-'}</TableCell><TableCell>{user.Email}</TableCell>
                <TableCell>{user.IsAdmin ? <Chip label="Admin" color="secondary" size="small" /> : <Chip label="User" size="small" />}</TableCell>
                <TableCell>{user.IsActive ? <Chip label="Active" color="success" size="small" /> : <Chip label="Inactive" size="small" />}</TableCell>
                <TableCell>{user.LastLoginAt ? new Date(user.LastLoginAt).toLocaleString() : 'Never'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(user)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(user.UserId)} size="small" color="error" disabled={user.UserId === currentUser?.userId}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" size="small" label="Username" fullWidth value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            disabled={!!editingUser} helperText={editingUser ? 'Username cannot be changed' : undefined} required={!editingUser} />
          <TextField margin="dense" size="small" label="Email" fullWidth value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          <TextField margin="dense" size="small" label="Full Name" fullWidth value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })} />
          <FormControlLabel control={<Switch checked={formData.isAdmin} onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
            disabled={editingUser?.UserId === currentUser?.userId} />} label="Admin Privileges" />
          {editingUser?.UserId === currentUser?.userId && (<Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>You cannot modify your own admin status</Typography>)}
          {editingUser && (
            <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary"
            disabled={!formData.username || !formData.email}>
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate User"
        message="Are you sure you want to deactivate this user?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default Users;
